// =============================================================================
// Calendar header — month picker + actions on a single line, no wrap. The
// month/year wheel, desktop ‹ › « » stepping, and the shared-calendar /
// birthdays / settings / labels / new-event buttons. Calendar.js owns the
// state; this only renders it and reports taps.
//
// The action buttons are icon-only below lg: with the sidebar rail beside
// it an iPad in portrait leaves ~640px for this row, and the labelled
// buttons pushed the month name into an ellipsis.
// =============================================================================

import React from 'react';
import { ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, Plus, Tags, Cake, Users as UsersIcon, Settings as SettingsIcon } from 'lucide-react';
import { addMonths, subMonths, addYears, subYears } from 'date-fns';
import { formatDate as fmt } from '../../lib/dates';
import { useTranslation } from '../../lib/i18n';
import WheelPicker from './WheelPicker';

const CalendarHeader = ({
  currentDate,
  showMonthPicker,
  setShowMonthPicker,
  monthLabels,
  yearOptions,
  onChangeMonthYear,
  onSlideTo,
  isSharedView,
  currentSharedCalendar,
  onCreateShared,
  onShowBirthdays,
  onOpenSettings,
  onOpenLabelManager,
  onAddEvent
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-3 min-w-0">
        {/* Desktop month/year stepping — phones swipe instead. */}
        <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => onSlideTo(subYears(currentDate, 1))}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800"
            title={t('calendar.prevYear', 'Vorheriges Jahr')}
            aria-label={t('calendar.prevYear', 'Vorheriges Jahr')}
          >
            <ChevronsLeft size={20} />
          </button>
          <button
            type="button"
            onClick={() => onSlideTo(subMonths(currentDate, 1))}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800"
            title={t('calendar.prevMonth', 'Vorheriger Monat')}
            aria-label={t('calendar.prevMonth', 'Vorheriger Monat')}
          >
            <ChevronLeft size={20} />
          </button>
        </div>
        {/* Month/Year selector — iOS-style dual wheel picker */}
        <div className="relative min-w-0">
          <button
            onClick={() => setShowMonthPicker(!showMonthPicker)}
            className="flex items-center gap-1 sm:gap-2 text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 hover:text-primary-600 transition-colors truncate"
          >
            <span className="truncate">
              {fmt(currentDate, 'monthYear')}
            </span>
            <ChevronRight
              size={20}
              className={`flex-shrink-0 transition-transform ${showMonthPicker ? 'rotate-90' : ''}`}
            />
          </button>
          {showMonthPicker && (
            <>
              {/* Click-away backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMonthPicker(false)}
                aria-hidden="true"
              />
              <WheelPicker
                months={monthLabels}
                years={yearOptions}
                selectedMonth={currentDate.getMonth()}
                selectedYear={currentDate.getFullYear()}
                onChange={onChangeMonthYear}
              />
            </>
          )}
        </div>
        <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => onSlideTo(addMonths(currentDate, 1))}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800"
            title={t('calendar.nextMonth', 'Nächster Monat')}
            aria-label={t('calendar.nextMonth', 'Nächster Monat')}
          >
            <ChevronRight size={20} />
          </button>
          <button
            type="button"
            onClick={() => onSlideTo(addYears(currentDate, 1))}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800"
            title={t('calendar.nextYear', 'Nächstes Jahr')}
            aria-label={t('calendar.nextYear', 'Nächstes Jahr')}
          >
            <ChevronsRight size={20} />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 justify-end">
        {!isSharedView && (
          <button
            onClick={onCreateShared}
            className="btn-secondary !px-2 !py-1.5 sm:!py-2 lg:!px-4 flex items-center gap-2"
            title={t('sharedCalendar.createButton')}
          >
            <UsersIcon size={16} className="sm:hidden" />
            <UsersIcon size={18} className="hidden sm:inline-block" />
            <span className="hidden lg:inline">{t('sharedCalendar.createButton')}</span>
          </button>
        )}
        {isSharedView && currentSharedCalendar && currentSharedCalendar.hasBirthdays && (
          <button
            onClick={onShowBirthdays}
            className="btn-secondary !px-2 !py-1.5 sm:!py-2 lg:!px-4 flex items-center gap-2"
            title={t('sharedCalendar.manageBirthdays')}
          >
            <Cake size={16} className="sm:hidden" />
            <Cake size={18} className="hidden sm:inline-block" />
            <span className="hidden lg:inline">{t('sharedCalendar.birthdays')}</span>
          </button>
        )}
        {isSharedView && currentSharedCalendar && (
          <button
            onClick={onOpenSettings}
            className="btn-secondary !px-2 !py-1.5 sm:!py-2 lg:!px-4 flex items-center gap-2"
            title={t('sharedCalendar.settings')}
            aria-label={t('sharedCalendar.settings')}
          >
            <SettingsIcon size={16} className="sm:hidden" />
            <SettingsIcon size={18} className="hidden sm:inline-block" />
          </button>
        )}
        <button
          onClick={onOpenLabelManager}
          className="btn-secondary !px-2 !py-1.5 sm:!py-2 lg:!px-4 flex items-center gap-2"
          title={t('calendar.manageLabels')}
        >
          <Tags size={16} className="sm:hidden" />
          <Tags size={18} className="hidden sm:inline-block" />
          <span className="hidden lg:inline">{t('calendar.labels')}</span>
        </button>
        <button
          onClick={onAddEvent}
          className="hidden sm:flex btn bg-primary-600 text-white hover:bg-primary-700 items-center gap-2"
        >
          <Plus size={18} />
          {t('calendar.event')}
        </button>
      </div>
    </div>
  );
};

export default CalendarHeader;
